import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { crearTallerBase, crearTallerSobrecargado } from '../../fixtures/plantillas';
import { crearTallerDemo } from '../../fixtures/tallerDemo';
import type { Proyecto } from '../../nucleo/dominio/espacio';
import {
  COLORES_DE_CARPETA,
  ESPACIO_DE_TRABAJO_PROPIO,
  puedeLlevarse,
  type Carpeta,
  type EspacioDeTrabajo,
} from '../../nucleo/dominio/organizacion';
import { importarArchivo } from '../../persistencia/archivo';
import { ReglaIncumplida, type ResumenProyecto } from '../../persistencia/repositorio';
import { ahoraIso, fechaHora } from '../formato';
import { M } from '../mensajes/es-PE';
import { exportarArchivo } from '../servicios/exportar';
import { nuevoId, repositorio } from '../servicios/repositorio';
import {
  carpetaDeDestino,
  guardarUbicacion,
  leerUbicacion,
  ubicarProyectoNuevo,
  type Ubicacion,
} from '../servicios/ubicacion';
import { avisar } from '../ui/avisos';
import { confirmar } from '../ui/Confirmaciones';
import { DialogoConfirmar } from '../ui/Dialogo';
import { Icono, Marca } from '../ui/Icono';
import { anunciarGuardado } from '../proyecto/pestanas';
import { MedidorAlmacenamiento } from './Almacenamiento';
import { MiniaturaProyecto } from './Miniatura';
import {
  DialogoCarpeta,
  DialogoEspacioDeTrabajo,
  DialogoOpcionesProyecto,
  InsigniaDatosReales,
  type CambiosDeProyecto,
} from './Organizacion';

const O = M.organizacion;

function etiquetaOrigen(origen: string): string {
  return M.inicio.origen[origen.startsWith('caso:') ? 'caso' : origen] ?? origen;
}

/** Sin tildes ni mayúsculas, para buscar por nombre. */
const normalizar = (t: string) => t.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

const mensajeDe = (e: unknown) => (e instanceof Error ? e.message : String(e));

export function PaginaInicio() {
  const navegar = useNavigate();
  const [proyectos, setProyectos] = useState<ResumenProyecto[] | null>(null);
  const [espacios, setEspacios] = useState<EspacioDeTrabajo[]>([]);
  const [carpetas, setCarpetas] = useState<Carpeta[]>([]);
  const [ubicacion, setUbicacionLocal] = useState<Ubicacion>(leerUbicacion);
  const [busqueda, setBusqueda] = useState('');
  const [aEliminar, setAEliminar] = useState<ResumenProyecto | null>(null);
  const [opciones, setOpciones] = useState<Proyecto | null>(null);
  const [editandoEspacio, setEditandoEspacio] = useState<EspacioDeTrabajo | 'nuevo' | null>(null);
  const [editandoCarpeta, setEditandoCarpeta] = useState<Carpeta | 'nueva' | null>(null);
  const [lecturas, setLecturas] = useState(0);
  const archivo = useRef<HTMLInputElement>(null);

  const setUbicacion = useCallback((u: Ubicacion) => {
    setUbicacionLocal(u);
    guardarUbicacion(u);
  }, []);

  const cargar = useCallback(async () => {
    try {
      const [lista, todos] = await Promise.all([
        repositorio().listar(),
        repositorio().listarEspaciosDeTrabajo(ahoraIso()),
      ]);
      setProyectos(lista);
      setEspacios(todos);
      setLecturas((n) => n + 1);
    } catch (e) {
      setProyectos([]);
      avisar(`No se pudo leer el almacenamiento del navegador: ${mensajeDe(e)}`, 'error');
    }
  }, []);

  useEffect(() => {
    void cargar();
  }, [cargar]);

  // Si el espacio de trabajo recordado ya no existe, se vuelve a «Mis proyectos».
  const espacio =
    espacios.find((e) => e.id === ubicacion.espacioDeTrabajoId) ??
    espacios.find((e) => e.id === ESPACIO_DE_TRABAJO_PROPIO) ??
    null;
  const espacioId = espacio?.id ?? ESPACIO_DE_TRABAJO_PROPIO;
  useEffect(() => {
    if (espacio && espacio.id !== ubicacion.espacioDeTrabajoId)
      setUbicacion({ espacioDeTrabajoId: espacio.id, carpeta: 'todas' });
  }, [espacio, ubicacion.espacioDeTrabajoId, setUbicacion]);

  const cargarCarpetas = useCallback(async () => {
    setCarpetas(await repositorio().listarCarpetas(espacioId));
  }, [espacioId]);
  useEffect(() => {
    void cargarCarpetas();
  }, [cargarCarpetas, lecturas]);

  const carpeta =
    carpetaDeDestino(ubicacion) !== null
      ? (carpetas.find((c) => c.id === ubicacion.carpeta) ?? null)
      : null;

  const delEspacio = useMemo(
    () => (proyectos ?? []).filter((p) => p.espacioDeTrabajoId === espacioId),
    [proyectos, espacioId],
  );
  const q = normalizar(busqueda.trim());
  const visibles = q
    ? delEspacio.filter((p) => normalizar(p.nombre).includes(q))
    : delEspacio.filter((p) =>
        ubicacion.carpeta === 'todas'
          ? true
          : ubicacion.carpeta === 'sin-carpeta'
            ? !p.carpetaId || !carpetas.some((c) => c.id === p.carpetaId)
            : p.carpetaId === ubicacion.carpeta,
      );
  const lugar = O.lugar(espacio?.nombre ?? '', carpeta?.nombre ?? null);

  const guardarYAbrir = async (nuevo: Proyecto) => {
    try {
      const { proyecto } = await ubicarProyectoNuevo(nuevo);
      await repositorio().guardar(proyecto);
      void navegar(`/proyecto/${proyecto.id}`);
    } catch (e) {
      avisar(mensajeDe(e), 'error');
    }
  };

  const importar = async (f: File) => {
    const r = importarArchivo(await f.text(), { nuevoId, ahora: ahoraIso() });
    if (!r.ok) {
      avisar([r.mensaje, ...r.detalles.slice(0, 3)].join(' '), 'error');
      return;
    }
    const ubicado = await ubicarProyectoNuevo(r.proyecto);
    // Regla de integridad 7: un archivo con datos reales solo entra a un espacio de trabajo marcado.
    if (
      !puedeLlevarse(r.contieneDatosReales ?? false, { contieneDatosReales: ubicado.datosReales })
        .ok
    ) {
      avisar(O.importarDatosReales, 'error');
      return;
    }
    let p = ubicado.proyecto;
    let copia = false;
    if (await repositorio().existe(p.id)) {
      p = { ...p, id: nuevoId(), nombre: `${p.nombre} (copia)` };
      copia = true;
    }
    p = { ...p, actualizadoEn: ahoraIso() };
    await repositorio().guardar(p);
    if (r.desdeV6) {
      // Resumen de la conversión y lo que no tuvo equivalente, antes de abrirlo.
      const elementos = p.espacios.reduce((a, e) => a + e.layout.elementos.length, 0);
      const abrir = await confirmar({
        titulo: M.inicio.importadoV6Titulo,
        texto: [
          M.inicio.importadoV6Texto(p.nombre, p.espacios.length, elementos),
          ...(r.desdeV6.avisos.length
            ? ['', M.inicio.importadoV6Revisa, ...r.desdeV6.avisos.map((a) => `• ${a}`)]
            : []),
        ].join('\n'),
        confirmar: M.inicio.abrirProyecto,
        cancelar: M.inicio.quedarmeEnElInicio,
      });
      if (abrir) void navegar(`/proyecto/${p.id}`);
      else void cargar();
      return;
    }
    avisar(
      copia
        ? M.inicio.importadoCopia(p.nombre)
        : r.migradoDesde
          ? M.inicio.importadoMigrado(p.nombre, r.migradoDesde)
          : M.inicio.importado(p.nombre),
      'exito',
    );
    void navegar(`/proyecto/${p.id}`);
  };

  const exportar = async (id: string) => {
    const p = await repositorio().obtener(id);
    if (p) await exportarArchivo(p, p.nombre, p.espacioDeTrabajoId);
  };

  const eliminar = async (p: ResumenProyecto) => {
    await repositorio().eliminar(p.id);
    setAEliminar(null);
    avisar(M.inicio.eliminado(p.nombre), 'info');
    void cargar();
  };

  const abrirOpciones = async (id: string) => {
    try {
      setOpciones(await repositorio().obtener(id));
    } catch (e) {
      avisar(mensajeDe(e), 'error');
    }
  };

  const guardarOpciones = async (p: Proyecto, c: CambiosDeProyecto) => {
    try {
      if (c.nombre !== p.nombre) {
        const iso = ahoraIso();
        await repositorio().guardar({ ...p, nombre: c.nombre, actualizadoEn: iso });
        // Si está abierto en otra pestaña, esa pestaña se entera y no lo pisa.
        anunciarGuardado(p.id, iso);
      }
      if (c.espacioDeTrabajoId !== p.espacioDeTrabajoId || c.carpetaId !== (p.carpetaId ?? null)) {
        await repositorio().mover(p.id, c.espacioDeTrabajoId, c.carpetaId);
        const destino = espacios.find((e) => e.id === c.espacioDeTrabajoId);
        const nombreCarpeta = c.carpetaId
          ? ((await repositorio().listarCarpetas(c.espacioDeTrabajoId)).find(
              (x) => x.id === c.carpetaId,
            )?.nombre ?? null)
          : null;
        avisar(O.movido(c.nombre, O.lugar(destino?.nombre ?? '', nombreCarpeta)), 'exito');
      } else avisar(O.guardado, 'exito');
      setOpciones(null);
      void cargar();
    } catch (e) {
      avisar(
        e instanceof ReglaIncumplida && e.motivo === 'datos-reales'
          ? O.noSaleDeDatosReales
          : mensajeDe(e),
        'error',
      );
    }
  };

  const duplicar = async (p: Proyecto) => {
    try {
      const copia = await repositorio().duplicar(p.id, nuevoId(), O.copia(p.nombre), ahoraIso());
      setOpciones(null);
      avisar(O.duplicado(copia.nombre), 'exito');
      void cargar();
    } catch (e) {
      avisar(mensajeDe(e), 'error');
    }
  };

  const guardarEspacio = async (e: EspacioDeTrabajo, nuevo: boolean) => {
    try {
      await repositorio().guardarEspacioDeTrabajo(e);
      setEditandoEspacio(null);
      // Primero la lista nueva: si no, el inicio no lo encuentra y vuelve a «Mis proyectos».
      await cargar();
      if (nuevo) {
        setUbicacion({ espacioDeTrabajoId: e.id, carpeta: 'todas' });
        avisar(O.espacioCreado(e.nombre), 'exito');
      }
    } catch (err) {
      avisar(mensajeDe(err), 'error');
    }
  };

  const eliminarEspacio = async (e: EspacioDeTrabajo) => {
    if (delEspacio.length > 0 && e.id === espacioId) {
      avisar(O.espacioConProyectos, 'advertencia');
      return;
    }
    if (
      !(await confirmar({
        titulo: O.eliminarEspacioTitulo(e.nombre),
        texto: O.eliminarEspacioTexto,
        confirmar: O.eliminarEspacio,
        peligro: true,
      }))
    )
      return;
    try {
      await repositorio().eliminarEspacioDeTrabajo(e.id);
      setEditandoEspacio(null);
      setUbicacion({ espacioDeTrabajoId: ESPACIO_DE_TRABAJO_PROPIO, carpeta: 'todas' });
      avisar(O.espacioEliminado(e.nombre), 'info');
      void cargar();
    } catch (err) {
      avisar(
        err instanceof ReglaIncumplida && err.motivo === 'espacio-con-proyectos'
          ? O.espacioConProyectos
          : mensajeDe(err),
        'advertencia',
      );
    }
  };

  const guardarCarpeta = async (c: Carpeta, nueva: boolean) => {
    try {
      await repositorio().guardarCarpeta(c);
      setEditandoCarpeta(null);
      if (nueva) setUbicacion({ espacioDeTrabajoId: c.espacioDeTrabajoId, carpeta: c.id });
      await cargarCarpetas();
    } catch (e) {
      avisar(mensajeDe(e), 'error');
    }
  };

  const eliminarCarpeta = async (c: Carpeta) => {
    if (
      !(await confirmar({
        titulo: O.eliminarCarpetaTitulo(c.nombre),
        texto: O.eliminarCarpetaTexto,
        confirmar: O.eliminarCarpeta,
        peligro: true,
      }))
    )
      return;
    await repositorio().eliminarCarpeta(c.id);
    setEditandoCarpeta(null);
    setUbicacion({ espacioDeTrabajoId: espacioId, carpeta: 'todas' });
    avisar(O.carpetaEliminada(c.nombre), 'info');
    void cargar();
  };

  const contar = (filtro: (p: ResumenProyecto) => boolean) => delEspacio.filter(filtro).length;
  const carpetaDe = (id: string | null) => carpetas.find((c) => c.id === id) ?? null;

  return (
    <div className="pagina-inicio">
      <header className="cabecera-inicio">
        <Link to="/" className="marca">
          <Marca />
          {M.app.nombre}
        </Link>
        <span className="atenuado num" title={M.app.acercaDe}>
          {M.app.version(__VERSION_APP__, __COMMIT_APP__)}
        </span>
      </header>

      <div className="cuerpo-inicio">
        <nav className="lateral-inicio" aria-label={O.lateral}>
          <section className="grupo-lateral">
            <div className="titulo-lateral">
              <h2 className="titulo-seccion">{O.espaciosDeTrabajo}</h2>
              <button
                type="button"
                className="boton icono chico"
                onClick={() => setEditandoEspacio('nuevo')}
                aria-label={O.nuevoEspacio}
                title={O.nuevoEspacio}
              >
                <Icono nombre="mas" tamano={16} />
              </button>
            </div>
            <ul className="lista-lateral" data-testid="espacios-de-trabajo">
              {espacios.map((e) => (
                <li key={e.id}>
                  <button
                    type="button"
                    className="item-lateral"
                    aria-pressed={e.id === espacioId}
                    onClick={() => {
                      setBusqueda('');
                      setUbicacion({ espacioDeTrabajoId: e.id, carpeta: 'todas' });
                    }}
                  >
                    <Icono nombre={e.tipo === 'cliente' ? 'oficina' : 'planta'} tamano={16} />
                    <span className="nombre-item" title={e.nombre}>
                      {e.nombre}
                    </span>
                    {e.contieneDatosReales ? <InsigniaDatosReales /> : null}
                    <span className="conteo num">
                      {(proyectos ?? []).filter((p) => p.espacioDeTrabajoId === e.id).length}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          </section>

          <section className="grupo-lateral">
            <div className="titulo-lateral">
              <h2 className="titulo-seccion">{O.carpetas}</h2>
              <button
                type="button"
                className="boton icono chico"
                onClick={() => setEditandoCarpeta('nueva')}
                aria-label={O.nuevaCarpeta}
                title={O.nuevaCarpeta}
              >
                <Icono nombre="mas" tamano={16} />
              </button>
            </div>
            <ul className="lista-lateral" data-testid="carpetas">
              {(
                [
                  ['todas', O.todas, contar(() => true)],
                  [
                    'sin-carpeta',
                    O.sinCarpeta,
                    contar((p) => !p.carpetaId || !carpetaDe(p.carpetaId)),
                  ],
                ] as const
              ).map(([id, nombre, n]) => (
                <li key={id}>
                  <button
                    type="button"
                    className="item-lateral"
                    aria-pressed={!q && ubicacion.carpeta === id}
                    onClick={() => {
                      setBusqueda('');
                      setUbicacion({ espacioDeTrabajoId: espacioId, carpeta: id });
                    }}
                  >
                    <span className="nombre-item">{nombre}</span>
                    <span className="conteo num">{n}</span>
                  </button>
                </li>
              ))}
              {carpetas.map((c) => (
                <li key={c.id} className="fila-carpeta">
                  <button
                    type="button"
                    className="item-lateral"
                    aria-pressed={!q && ubicacion.carpeta === c.id}
                    onClick={() => {
                      setBusqueda('');
                      setUbicacion({ espacioDeTrabajoId: espacioId, carpeta: c.id });
                    }}
                  >
                    <span className="punto-carpeta" style={{ background: c.color }} />
                    <span className="nombre-item" title={c.nombre}>
                      {c.nombre}
                    </span>
                    <span className="conteo num">{contar((p) => p.carpetaId === c.id)}</span>
                  </button>
                  <button
                    type="button"
                    className="boton icono chico"
                    onClick={() => setEditandoCarpeta(c)}
                    aria-label={O.editarCarpetaAyuda(c.nombre)}
                    title={O.editarCarpetaAyuda(c.nombre)}
                  >
                    <Icono nombre="ajustes" tamano={16} />
                  </button>
                </li>
              ))}
            </ul>
          </section>
        </nav>

        <main className="contenido-inicio">
          <section className="portada">
            <h1>{M.inicio.titulo}</h1>
            <p className="secundario">{M.inicio.subtitulo}</p>
          </section>

          <section aria-labelledby="titulo-empezar" className="seccion-inicio">
            <div className="titulo-con-nota">
              <h2 id="titulo-empezar" className="titulo-seccion">
                {M.inicio.empezar}
              </h2>
              <span className="atenuado" data-testid="lugar-de-creacion">
                {O.creaEn(lugar)}
              </span>
            </div>
            <div className="tarjetas-plantilla">
              <article className="panel tarjeta-plantilla destacada">
                <MiniaturaTaller />
                <h3>{M.inicio.tallerBase}</h3>
                <p className="secundario">{M.inicio.tallerBaseDesc}</p>
                <button
                  type="button"
                  className="boton principal"
                  onClick={() => void guardarYAbrir(crearTallerBase(nuevoId, ahoraIso()))}
                >
                  {M.inicio.crearTallerBase}
                  <Icono nombre="flecha" />
                </button>
                <button
                  type="button"
                  className="boton fantasma chico"
                  title={M.inicio.sobrecargadoDesc}
                  onClick={() => void guardarYAbrir(crearTallerSobrecargado(nuevoId, ahoraIso()))}
                >
                  {M.inicio.crearSobrecargado}
                </button>
                <button
                  type="button"
                  className="boton fantasma chico"
                  title={M.inicio.tallerDemoDesc}
                  data-testid="crear-taller-demo"
                  onClick={() => void guardarYAbrir(crearTallerDemo(nuevoId, ahoraIso()))}
                >
                  {M.inicio.crearTallerDemo}
                </button>
              </article>
              <article className="panel tarjeta-plantilla">
                <div className="miniatura vacia" aria-hidden="true" />
                <h3>{M.inicio.lienzo}</h3>
                <p className="secundario">{M.inicio.lienzoDesc}</p>
                <button type="button" className="boton" onClick={() => void navegar('/configurar')}>
                  <Icono nombre="mas" />
                  {M.inicio.crearLienzo}
                </button>
              </article>
              <article className="panel tarjeta-plantilla">
                <div className="miniatura importar" aria-hidden="true">
                  <Icono nombre="importar" tamano={36} />
                </div>
                <h3>{M.inicio.importar}</h3>
                <p className="secundario">{M.inicio.importarDesc}</p>
                <button type="button" className="boton" onClick={() => archivo.current?.click()}>
                  <Icono nombre="importar" />
                  {M.inicio.importar}
                </button>
                <input
                  ref={archivo}
                  type="file"
                  accept=".json,.gemba.json,application/json"
                  className="oculto-visual"
                  data-testid="entrada-importar"
                  aria-label={M.inicio.importar}
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    e.target.value = '';
                    if (f) void importar(f);
                  }}
                />
              </article>
            </div>
          </section>

          <section aria-labelledby="titulo-proyectos" className="seccion-inicio">
            <div className="cabecera-proyectos">
              <div className="titulo-espacio">
                <h2 id="titulo-proyectos" className="titulo-lugar">
                  {espacio ? lugar : M.inicio.tusProyectos}
                </h2>
                {espacio ? (
                  <span className="insignia">
                    {espacio.tipo === 'cliente' ? O.cliente : O.propio}
                  </span>
                ) : null}
                {espacio?.contieneDatosReales ? <InsigniaDatosReales largo /> : null}
                {espacio ? (
                  <button
                    type="button"
                    className="boton chico"
                    onClick={() => setEditandoEspacio(espacio)}
                    aria-label={O.configurarAyuda(espacio.nombre)}
                    title={O.configurarAyuda(espacio.nombre)}
                  >
                    <Icono nombre="ajustes" tamano={16} />
                    {O.configurar}
                  </button>
                ) : null}
              </div>
              <label className="buscador">
                <Icono nombre="buscar" tamano={16} />
                <input
                  type="search"
                  className="entrada"
                  value={busqueda}
                  placeholder={O.buscarEjemplo}
                  aria-label={O.buscar}
                  onChange={(e) => setBusqueda(e.target.value)}
                />
                {q ? (
                  <span className="atenuado num">
                    {O.resultados(visibles.length, delEspacio.length)}
                  </span>
                ) : null}
              </label>
            </div>
            {proyectos === null ? (
              <p className="atenuado" role="status">
                {M.app.cargando}
              </p>
            ) : visibles.length === 0 ? (
              <div className="panel vacio-proyectos">
                {q ? (
                  <p>{O.sinResultados(busqueda.trim())}</p>
                ) : carpeta || ubicacion.carpeta === 'sin-carpeta' ? (
                  <>
                    <p>
                      <strong>{O.carpetaVacia}</strong>
                    </p>
                    <p className="secundario">{O.carpetaVaciaSiguiente}</p>
                  </>
                ) : (
                  <>
                    <p>
                      <strong>{M.inicio.vacio}</strong>
                    </p>
                    <p className="secundario">{M.inicio.vacioSiguiente}</p>
                  </>
                )}
              </div>
            ) : (
              <ul className="lista-proyectos" aria-label={M.inicio.tusProyectos}>
                {visibles.map((p) => {
                  const c = carpetaDe(p.carpetaId);
                  return (
                    <li key={p.id} className="panel fila-proyecto" data-testid="proyecto">
                      <Link to={`/proyecto/${p.id}`} className="enlace-miniatura" tabIndex={-1}>
                        <MiniaturaProyecto planta={p.planta} />
                      </Link>
                      <div className="datos-proyecto">
                        <Link to={`/proyecto/${p.id}`} className="nombre-proyecto">
                          {p.nombre}
                        </Link>
                        <span className="secundario">
                          <span className="insignia">{etiquetaOrigen(p.origen)}</span>
                          {c && (ubicacion.carpeta !== c.id || q) ? (
                            <>
                              {' '}
                              <span className="insignia carpeta">
                                <span className="punto-carpeta" style={{ background: c.color }} />
                                {c.nombre}
                              </span>
                            </>
                          ) : null}{' '}
                          · {M.inicio.elementos(p.elementos)} ·{' '}
                          {M.inicio.actualizado(fechaHora(p.actualizadoEn))}
                        </span>
                      </div>
                      <div className="acciones-proyecto">
                        <button
                          type="button"
                          className="boton principal chico"
                          onClick={() => void navegar(`/proyecto/${p.id}`)}
                        >
                          {M.inicio.abrir}
                        </button>
                        <button
                          type="button"
                          className="boton chico"
                          onClick={() => void abrirOpciones(p.id)}
                          aria-label={O.opcionesAyuda(p.nombre)}
                          title={O.opcionesAyuda(p.nombre)}
                        >
                          <Icono nombre="ajustes" tamano={16} />
                          {O.opciones}
                        </button>
                        <button
                          type="button"
                          className="boton chico"
                          onClick={() => void exportar(p.id)}
                          aria-label={`${M.inicio.exportar} ${p.nombre}`}
                        >
                          <Icono nombre="exportar" tamano={16} />
                          {M.inicio.exportar}
                        </button>
                        <button
                          type="button"
                          className="boton peligro chico"
                          onClick={() => setAEliminar(p)}
                          aria-label={`${M.inicio.eliminar} ${p.nombre}`}
                        >
                          <Icono nombre="eliminar" tamano={16} />
                          {M.inicio.eliminar}
                        </button>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </section>

          <MedidorAlmacenamiento actualizar={lecturas} />
        </main>
      </div>

      <footer className="pie-inicio">
        <p className="secundario">{M.inicio.privacidad}</p>
        <Link to="/diagnostico">{M.inicio.diagnostico}</Link>
      </footer>

      {aEliminar ? (
        <DialogoConfirmar
          titulo={M.inicio.eliminarTitulo(aEliminar.nombre)}
          textoConfirmar={M.inicio.eliminar}
          peligro
          alConfirmar={() => void eliminar(aEliminar)}
          alCancelar={() => setAEliminar(null)}
        >
          {M.inicio.eliminarTexto}
        </DialogoConfirmar>
      ) : null}
      {opciones ? (
        <DialogoOpcionesProyecto
          proyecto={opciones}
          espacios={espacios}
          carpetasDe={(id) => repositorio().listarCarpetas(id)}
          alGuardar={(c) => void guardarOpciones(opciones, c)}
          alDuplicar={() => void duplicar(opciones)}
          alExportar={() =>
            void exportarArchivo(opciones, opciones.nombre, opciones.espacioDeTrabajoId)
          }
          alEliminar={() => {
            const r = (proyectos ?? []).find((x) => x.id === opciones.id) ?? null;
            setOpciones(null);
            setAEliminar(r);
          }}
          alCerrar={() => setOpciones(null)}
        />
      ) : null}
      {editandoEspacio ? (
        <DialogoEspacioDeTrabajo
          espacio={editandoEspacio === 'nuevo' ? null : editandoEspacio}
          ahora={ahoraIso()}
          nuevoId={nuevoId}
          alGuardar={(e) => void guardarEspacio(e, editandoEspacio === 'nuevo')}
          alEliminar={(e) => void eliminarEspacio(e)}
          alCerrar={() => setEditandoEspacio(null)}
        />
      ) : null}
      {editandoCarpeta ? (
        <DialogoCarpeta
          carpeta={editandoCarpeta === 'nueva' ? null : editandoCarpeta}
          espacioDeTrabajoId={espacioId}
          sugerido={COLORES_DE_CARPETA[carpetas.length % COLORES_DE_CARPETA.length]!}
          ahora={ahoraIso()}
          nuevoId={nuevoId}
          alGuardar={(c) => void guardarCarpeta(c, editandoCarpeta === 'nueva')}
          alEliminar={(c) => void eliminarCarpeta(c)}
          alCerrar={() => setEditandoCarpeta(null)}
        />
      ) : null}
    </div>
  );
}

/** Miniatura de planta en SVG del Taller base (barata y clara, como en el v6). */
function MiniaturaTaller() {
  return (
    <svg className="miniatura" viewBox="0 0 60 80" aria-hidden="true">
      <rect x="0" y="0" width="60" height="80" rx="3" fill="#F3EFE9" />
      <g stroke="#D6CFC4" strokeWidth="3" strokeLinecap="round">
        <path d="M53 2v76M53 30H30M30 27v7M20 8v58M9 8h11M9 8v58M9 66h27M36 66V34M36 34h17M20 38h16" />
      </g>
      <g fill="#B7B0A6">
        {[12, 16, 20, 24, 28, 32].map((z) => (
          <rect key={z} x="1.2" y={z - 1.6} width="6" height="3.2" rx="0.6" />
        ))}
      </g>
      <g fill="#9EAAB9">
        <rect x="22.5" y="26.6" width="5" height="2.4" rx="0.5" />
        <rect x="22.5" y="29.6" width="5" height="2.4" rx="0.5" />
      </g>
      <g fill="#DCD6CD">
        {[42, 45, 48, 51, 54, 57].map((z) => (
          <rect key={z} x="22" y={z - 1.2} width="5" height="2.4" rx="0.5" />
        ))}
      </g>
      <rect x="11" y="69" width="6" height="6" rx="0.8" fill="#9DB3BC" />
      <rect x="22" y="12.5" width="10" height="7" rx="1" fill="#FBF9F6" stroke="#D6CFC4" />
    </svg>
  );
}
